import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PRIMARY_VISIT_SECTION_DEFS,
  VITAL_FIELD_KEYS,
  resolveLocalized,
  type DiagnosisCandidate,
} from "@arna-med/shared";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertTriangle,
  BookMarked,
  Check,
  CheckCircle2,
  Loader2,
  Search,
  Stethoscope,
} from "lucide-react";
import { useProtocolSearch } from "@/hooks/api/useClinicalProtocols";

type Props = {
  /** Sections still awaiting the doctor, computed by `unconfirmedSections`. */
  pending: string[];
  confirmedCount: number;
  candidates: DiagnosisCandidate[];
  /** structuredData + editedData, already merged by the report screen. */
  data: Record<string, unknown>;
  missingFields: string[];
  language: string;
  onSetDiagnosis: (payload: {
    diagnosisMain: string;
    diagnosisIcd10?: string | null;
    protocolId?: string | null;
  }) => void;
  onConfirmSection: (sectionKey: string) => void;
  isSettingDiagnosis: boolean;
  isConfirming: boolean;
};

const SECTION_TITLES = new Map(
  PRIMARY_VISIT_SECTION_DEFS.map((s) => [s.key, s.title]),
);

/**
 * The three decisions a primary visit puts in front of the doctor: the
 * diagnosis, the measurements nobody else can supply, and the sections the
 * system wrote for them.
 */
export function PrimaryVisitPanel({
  pending,
  confirmedCount,
  candidates,
  data,
  missingFields,
  language,
  onSetDiagnosis,
  onConfirmSection,
  isSettingDiagnosis,
  isConfirming,
}: Props) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: searchResults = [], isFetching } = useProtocolSearch(
    searchTerm,
    language,
  );

  const currentDiagnosis = String(data.diagnosisMain ?? "").trim();
  const currentIcd10 = String(data.diagnosisIcd10 ?? "").trim();
  const missingVitals = VITAL_FIELD_KEYS.filter((key) =>
    missingFields.includes(key),
  );

  const sectionTitle = (key: string) =>
    resolveLocalized(SECTION_TITLES.get(key as never), i18n.language) || key;

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-primary" />
          {t("primaryVisit.panelTitle")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t("primaryVisit.panelHint")}
        </p>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ── Diagnosis: proposed by the model, decided by the doctor ── */}
        <div className="space-y-2">
          <p className="text-sm font-semibold">
            {t("primaryVisit.diagnosisTitle")}
          </p>

          {currentDiagnosis ? (
            <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span className="font-medium">{currentDiagnosis}</span>
              {currentIcd10 && (
                <span className="font-mono text-xs opacity-80">
                  {currentIcd10}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {t("primaryVisit.diagnosisNotSet")}
            </div>
          )}

          {candidates.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                {t("primaryVisit.candidatesHint")}
              </p>
              {candidates.map((candidate, index) => (
                <div
                  key={`${candidate.name}-${index}`}
                  className="flex items-start justify-between gap-3 rounded-md border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {candidate.name}
                      {candidate.icd10 && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {candidate.icd10}
                        </span>
                      )}
                    </p>
                    {candidate.rationale && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {candidate.rationale}
                      </p>
                    )}
                    {candidate.protocolId ? (
                      <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-violet-700">
                        <BookMarked className="h-3 w-3" />
                        {t("primaryVisit.hasProtocol")}
                      </p>
                    ) : (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {t("primaryVisit.noProtocol")}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={
                      currentDiagnosis === candidate.name ? "secondary" : "default"
                    }
                    disabled={isSettingDiagnosis}
                    onClick={() =>
                      onSetDiagnosis({
                        diagnosisMain: candidate.name,
                        diagnosisIcd10: candidate.icd10 ?? null,
                        protocolId: candidate.protocolId ?? null,
                      })
                    }
                  >
                    {isSettingDiagnosis ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      t("primaryVisit.chooseDiagnosis")
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* The model's list is a shortcut, not a menu: any diagnosis in the
              protocol base can be picked instead. */}
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setSearchTerm(search);
                  }
                }}
                placeholder={t("primaryVisit.searchPlaceholder")}
                className="text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSearchTerm(search)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {isFetching && <Spinner className="py-2" />}
            {!isFetching && searchTerm.length >= 2 && searchResults.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t("primaryVisit.searchEmpty")}
              </p>
            )}
            {searchResults.length > 0 && (
              <ul className="max-h-56 overflow-y-auto rounded-md border divide-y">
                {searchResults.map((protocol) => (
                  <li
                    key={protocol.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm truncate">{protocol.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {protocol.icd10Codes.join(", ") ||
                          t("primaryVisit.noIcdCode")}
                        {" · "}
                        {protocol.language.toUpperCase()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isSettingDiagnosis}
                      onClick={() =>
                        onSetDiagnosis({
                          diagnosisMain: protocol.title,
                          diagnosisIcd10: protocol.icd10Codes[0] ?? null,
                          protocolId: protocol.id,
                        })
                      }
                    >
                      {t("primaryVisit.chooseDiagnosis")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ── The measurements nothing can fill in ── */}
        {missingVitals.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              {t("primaryVisit.vitalsMissingTitle")}
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              {t("primaryVisit.vitalsMissingHint")}
            </p>
            <p className="mt-1.5 text-xs font-mono text-amber-900">
              {missingVitals.map((key) => t(`primaryVisit.vitals.${key}`)).join(" · ")}
            </p>
          </div>
        )}

        {/* ── What the system wrote, waiting for the doctor ── */}
        {pending.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">
              {t("primaryVisit.confirmTitle")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("primaryVisit.confirmHint")}
            </p>
            {pending.map((sectionKey) => (
              <div
                key={sectionKey}
                className="flex items-center justify-between gap-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2"
              >
                <p className="text-sm text-sky-900">{sectionTitle(sectionKey)}</p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isConfirming}
                  onClick={() => onConfirmSection(sectionKey)}
                >
                  {isConfirming ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      {t("primaryVisit.confirmButton")}
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {pending.length === 0 && confirmedCount > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("primaryVisit.allConfirmed")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
